import { create } from 'zustand';
import type { User as FirebaseUser } from 'firebase/auth';
import type { MeResponse } from '../lib/api/auth';

export type AuthStatus = 'initializing' | 'signed-out' | 'signed-in';

interface AuthState {
  status: AuthStatus;
  firebaseUser: FirebaseUser | null;
  /** Decoded profile from /auth/me — fetched on sign-in. */
  profile: MeResponse | null;
  /** Set by main.tsx once Firebase's onAuthStateChanged fires for the first time. */
  setFirebaseUser: (u: FirebaseUser | null) => void;
  setProfile: (p: MeResponse | null) => void;
  reset: () => void;
}

/**
 * Auth state.
 *
 * Firebase Auth is the source of truth for "who is signed in". This store
 * is just a React-friendly mirror so components can subscribe without each
 * one calling `auth.onAuthStateChanged` itself. The profile comes from
 * `/auth/me` (server-side) — we keep both so the UI can render with the
 * Firebase user immediately and refine once the server replies.
 *
 * `status` lets the router show a splash while we wait for the first
 * Firebase auth callback (otherwise we'd briefly flash the welcome screen
 * to already-signed-in users on hard reload).
 */
export const useAuthStore = create<AuthState>((set) => ({
  status: 'initializing',
  firebaseUser: null,
  profile: null,
  setFirebaseUser: (u) =>
    set({
      firebaseUser: u,
      status: u ? 'signed-in' : 'signed-out',
      // Clear the profile if the user signed out so we never show stale data.
      profile: u ? undefined : null,
    } as Partial<AuthState>),
  setProfile: (p) => set({ profile: p }),
  reset: () => set({ status: 'signed-out', firebaseUser: null, profile: null }),
}));
